import React, { useEffect, useState } from 'react';
import {
  clearLogs,
  collectDeviceInfo,
  getPageRef,
  LogEntry,
  readLogs,
} from 'utils/logger';

// Support page: a customer opens /diagnostics on the affected phone and
// shares the report, so we can see the device details and recent errors.
const DiagnosticsPage: React.FC = () => {
  const [device, setDevice] = useState<Record<string, unknown>>();
  const [entries, setEntries] = useState<LogEntry[]>(() => readLogs());
  const [status, setStatus] = useState('');

  useEffect(() => {
    collectDeviceInfo().then(setDevice);
  }, []);

  const report = JSON.stringify(
    { generatedAt: new Date().toISOString(), pageRef: getPageRef(), device, entries },
    null,
    2,
  );

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(report);
      setStatus('Copied. Paste it into an email or chat to support.');
    } catch {
      setStatus('Copy failed. Please use Share instead.');
    }
  };

  const shareReport = async () => {
    const nav: any = navigator;
    if (!nav.share) {
      copyReport();
      return;
    }
    try {
      await nav.share({ title: 'ZAK Health diagnostics', text: report });
    } catch {
      // user cancelled
    }
  };

  const clear = () => {
    clearLogs();
    setEntries([]);
    setStatus('Logs cleared.');
  };

  const problems = entries.filter((e) => e.level !== 'info');

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 text-sm text-gray-800">
      <h1 className="text-xl font-semibold mb-1">Diagnostics</h1>
      <p className="text-gray-600 mb-4">
        {problems.length} warnings/errors, {entries.length} entries in total.
      </p>

      <div className="flex gap-2 mb-2">
        <button className="px-4 py-2 bg-red-700 text-white rounded-lg" onClick={shareReport}>
          Share
        </button>
        <button className="px-4 py-2 bg-gray-700 text-white rounded-lg" onClick={copyReport}>
          Copy
        </button>
        <button className="px-4 py-2 bg-gray-200 rounded-lg" onClick={clear}>
          Clear
        </button>
      </div>
      {status && <p className="text-gray-600 mb-4">{status}</p>}

      <h2 className="font-semibold mt-4 mb-2">Recent warnings and errors</h2>
      {problems.length === 0 && <p className="text-gray-500">None recorded.</p>}
      <ul className="space-y-2">
        {problems
          .slice(-30)
          .reverse()
          .map((e, i) => (
            <li key={i} className="bg-white rounded-lg p-3 shadow-sm break-words">
              <div className={e.level === 'error' ? 'text-red-700' : 'text-amber-700'}>
                {e.event}
              </div>
              <div className="text-xs text-gray-500">
                {e.ts} · {e.route} · Ref {e.pageRef}
              </div>
              {e.details && (
                <pre className="text-xs whitespace-pre-wrap mt-1">
                  {JSON.stringify(e.details, null, 2)}
                </pre>
              )}
            </li>
          ))}
      </ul>

      <h2 className="font-semibold mt-6 mb-2">Device</h2>
      <pre className="text-xs whitespace-pre-wrap bg-white rounded-lg p-3 shadow-sm break-words">
        {device ? JSON.stringify(device, null, 2) : 'Loading...'}
      </pre>
    </div>
  );
};

export default DiagnosticsPage;
